// Cadastro de uma entrada no cofre de certificados mTLS do lojista.
//
// Diferente do upload por configuração, aqui o par PEM não pertence a nenhuma
// configuração ainda: ele fica disponível para as configurações apontarem. Por
// isso o nome é obrigatório — é como o operador vai reconhecer o certificado no
// select do cadastro de processor.
//
// Duas formas de informar o par, como no painel de processors: enviar os
// arquivos (.crt/.pem + .key) ou colar o PEM. O arquivo é o caminho comum — é
// nesse formato que o banco entrega —, e colar existe para quem recebeu o
// conteúdo por outro meio.
//
// Em ambos os casos o par vai como TEXTO convertido para base64 no submit, sem
// multipart — mesmo contrato do upload por configuração.
import { zodResolver } from "@hookform/resolvers/zod"
import { UploadCloudIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  FileInput,
  FileUploader,
} from "@/components/ui/file-upload"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import {
  encodePemToBase64,
  PEM_CERTIFICATE_HEADER,
  PEM_PRIVATE_KEY_HEADER,
} from "@/features/processor-config/certificate-schemas"
import { useCreateMerchantMtlsCertificateMutation } from "@/services/admin/merchant-mtls-certificates"

const panelPrefix = "pages.adminMerchants"
const certificatePrefix =
  "pages.adminProcessorConfig.certificate"

// Lê o arquivo como TEXTO (PEM) — a conversão para base64 é no submit.
const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve(String(reader.result ?? ""))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })

const buildSchema = (messages: {
  required: string
  invalidCertificateHeader: string
  invalidKeyHeader: string
}) =>
  z.object({
    name: z.string().trim().min(1, messages.required),
    // O header é conferido no cliente para o operador não descobrir que
    // trocou os arquivos só depois de um 400 do backend.
    certificatePem: z
      .string()
      .trim()
      .min(1, messages.required)
      .refine(
        (value) => PEM_CERTIFICATE_HEADER.test(value),
        messages.invalidCertificateHeader
      ),
    privateKeyPem: z
      .string()
      .trim()
      .min(1, messages.required)
      .refine(
        (value) => PEM_PRIVATE_KEY_HEADER.test(value),
        messages.invalidKeyHeader
      ),
  })

type CreateMtlsCertificateValues = z.infer<
  ReturnType<typeof buildSchema>
>

type CreateMtlsCertificateDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const CreateMtlsCertificateDialog = ({
  open,
  onOpenChange,
}: CreateMtlsCertificateDialogProps) => {
  const { t } = useTranslation()
  const createMutation =
    useCreateMerchantMtlsCertificateMutation()

  const [certFiles, setCertFiles] = useState<File[] | null>(
    null
  )
  const [keyFiles, setKeyFiles] = useState<File[] | null>(
    null
  )

  const schema = useMemo(
    () =>
      buildSchema({
        // A copy de obrigatoriedade vive no painel de processors; sem isto o
        // operador veria a CHAVE crua na mensagem de erro.
        required: t(
          `pages.adminProcessorConfig.form.required`
        ),
        invalidCertificateHeader: t(
          `${certificatePrefix}.invalidCertificateHeader`
        ),
        invalidKeyHeader: t(
          `${certificatePrefix}.invalidKeyHeader`
        ),
      }),
    [t]
  )

  const form = useForm<CreateMtlsCertificateValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      certificatePem: "",
      privateKeyPem: "",
    },
  })

  const resetAll = () => {
    form.reset()
    setCertFiles(null)
    setKeyFiles(null)
  }

  // O arquivo alimenta o MESMO campo do formulário que a aba de colar. Assim a
  // validação de header e o submit são um só caminho — a aba escolhida muda
  // apenas como o texto entra.
  const handleFiles =
    (field: "certificatePem" | "privateKeyPem") =>
    async (files: File[] | null) => {
      const text = files?.[0]
        ? await readFileAsText(files[0])
        : ""
      if (field === "certificatePem") {
        setCertFiles(files)
      } else {
        setKeyFiles(files)
      }
      form.setValue(field, text, {
        shouldValidate: Boolean(text),
      })
    }

  const handleSubmit = form.handleSubmit((values) => {
    createMutation.mutate(
      {
        name: values.name,
        certificatePem: encodePemToBase64(
          values.certificatePem
        ),
        privateKeyPem: encodePemToBase64(
          values.privateKeyPem
        ),
      },
      {
        onSuccess: () => {
          resetAll()
          onOpenChange(false)
        },
      }
    )
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          resetAll()
        }
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t(
              `${panelPrefix}.mtlsCertificates.createTitle`
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              `${panelPrefix}.mtlsCertificates.createDescription`
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(
                      `${panelPrefix}.mtlsCertificates.name`
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Tabs defaultValue="upload">
              <TabsList>
                {/* Arquivo primeiro: é o formato em que o banco entrega. */}
                <TabsTrigger value="upload">
                  {t(`${certificatePrefix}.uploadTab`)}
                </TabsTrigger>
                <TabsTrigger value="paste">
                  {t(`${certificatePrefix}.pasteTab`)}
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="upload"
                className="space-y-4 pt-2"
              >
                <div className="space-y-1.5">
                  <Label>
                    {t(
                      `${certificatePrefix}.certificateFileLabel`
                    )}
                  </Label>
                  <FileUploader
                    value={certFiles}
                    onValueChange={handleFiles(
                      "certificatePem"
                    )}
                    dropzoneOptions={{
                      accept: {
                        "application/x-x509-ca-cert": [
                          ".crt",
                          ".pem",
                          ".cer",
                        ],
                      },
                      maxFiles: 1,
                      multiple: false,
                    }}
                  >
                    <FileInput className="rounded-lg border border-dashed">
                      <div className="flex flex-col items-center gap-1 py-4 text-center">
                        <UploadCloudIcon
                          className="size-5 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="text-xs text-muted-foreground">
                          {certFiles?.[0]?.name ??
                            t(
                              `${certificatePrefix}.certificateFileHint`
                            )}
                        </span>
                      </div>
                    </FileInput>
                  </FileUploader>
                </div>

                <div className="space-y-1.5">
                  <Label>
                    {/* Chave própria: no painel de processors a chave é opcional
                        (ausente = mantém a atual) e a copy de lá diz isso. No
                        cofre não existe "manter" — sem o par não há mTLS. */}
                    {t(
                      `${panelPrefix}.mtlsCertificates.privateKeyFileLabel`
                    )}
                  </Label>
                  <FileUploader
                    value={keyFiles}
                    onValueChange={handleFiles(
                      "privateKeyPem"
                    )}
                    dropzoneOptions={{
                      accept: {
                        "application/x-pem-file": [
                          ".key",
                          ".pem",
                        ],
                      },
                      maxFiles: 1,
                      multiple: false,
                    }}
                  >
                    <FileInput className="rounded-lg border border-dashed">
                      <div className="flex flex-col items-center gap-1 py-4 text-center">
                        <UploadCloudIcon
                          className="size-5 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="text-xs text-muted-foreground">
                          {keyFiles?.[0]?.name ??
                            t(
                              `${certificatePrefix}.privateKeyFileHint`
                            )}
                        </span>
                      </div>
                    </FileInput>
                  </FileUploader>
                </div>

                {/* Erro do arquivo aparece na aba de arquivo: o campo do
                    formulário é o mesmo, mas o Textarea está na outra aba e a
                    mensagem ficaria escondida. */}
                {form.formState.errors.certificatePem && (
                  <p className="text-sm font-medium text-destructive">
                    {
                      form.formState.errors.certificatePem
                        .message
                    }
                  </p>
                )}
                {form.formState.errors.privateKeyPem && (
                  <p className="text-sm font-medium text-destructive">
                    {
                      form.formState.errors.privateKeyPem
                        .message
                    }
                  </p>
                )}
              </TabsContent>

              <TabsContent
                value="paste"
                className="space-y-4 pt-2"
              >
                <FormField
                  control={form.control}
                  name="certificatePem"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          `${panelPrefix}.mtlsCertificates.certificatePem`
                        )}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={5}
                          className="font-mono text-xs"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="privateKeyPem"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t(
                          `${panelPrefix}.mtlsCertificates.privateKeyPem`
                        )}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={5}
                          className="font-mono text-xs"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
              >
                {t(`${panelPrefix}.mtlsCertificates.save`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
